import React, { useEffect, useRef } from "react";
import "./story.css";
import { ASSETS, CALENDLY_URL, PRODUCTION_URL } from "../config";
import { usePageSeo, absoluteUrl, ORGANISATION_JSON_LD } from "../useSeo";
import { Header, BookACallLink } from "../Header";
import { FloatingCta, Footer } from "../FooterAndFloating";
import { StoryActs, StoryPraise } from "./StorySections";

const DESCRIPTION =
  "Turn your business mission or entrepreneurial story into an original professional stage play, from first interview and rehearsed reading to full production.";

const PAGE = {
  title: "Put Your Story on Stage | The Business Playwright",
  description: DESCRIPTION,
  path: "/put-your-story-on-stage",
  ogImage: ASSETS.logo,
  ogImageAlt: "The Business Playwright logo",
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Put Your Story on Stage",
    url: absoluteUrl("/put-your-story-on-stage"),
    description: DESCRIPTION,
    isPartOf: { "@type": "WebSite", name: "The Business Playwright", url: PRODUCTION_URL },
    publisher: ORGANISATION_JSON_LD,
    mainEntity: {
      "@type": "Service",
      name: "Put Your Story on Stage",
      serviceType: "Original stage play about a business or entrepreneurial story",
      provider: ORGANISATION_JSON_LD,
      url: absoluteUrl("/put-your-story-on-stage"),
    },
    potentialAction: { "@type": "ReserveAction", target: CALENDLY_URL },
  },
};

const AI_PROOF = ["Your own unique life experience.", "Genuine human storytelling.", "Live in-person performance."];

export default function PutYourStoryOnStagePage() {
  usePageSeo(PAGE);
  const heroRef = useRef(null);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bp-site st-page" data-testid="story-page">
      <Header />
      <main id="main">
        <section className="st-hero" ref={heroRef} aria-labelledby="st-hero-title" data-testid="st-hero">
          <div className="bp-wrap st-hero-inner">
            <span className="bp-hero-rule" aria-hidden="true" />
            <h1 id="st-hero-title" className="st-hero-title" data-testid="st-hero-title">
              We turn business missions and entrepreneurial stories into <span className="st-hero-accent">professional stage plays.</span>
            </h1>
            <BookACallLink testId="st-hero-cta" />
          </div>
        </section>

        <section className="bp-scene st-scene" aria-labelledby="st-why-title" data-testid="st-why">
          <div className="bp-wrap">
            <h2 id="st-why-title" className="st-h2">Why theatre and why now?</h2>
            <p className="st-body st-narrow">
              In the age of AI, human stories have never been more important. And yet it has never been harder to stand out. Thankfully, there are three things AI cannot replace:
            </p>
            <ol className="st-three" data-testid="st-ai-proof">
              {AI_PROOF.map((t, i) => (
                <li key={t} className="st-three-item">
                  <span className="st-three-num" aria-hidden="true">{i + 1}</span>
                  <p>{t}</p>
                </li>
              ))}
            </ol>
            <p className="st-emphasis" data-testid="st-why-emphasis">Only a well-crafted stage play can deliver all three.</p>
          </div>
        </section>

        <section className="bp-scene st-scene" aria-labelledby="st-how-title" data-testid="st-how">
          <div className="bp-wrap">
            <h2 id="st-how-title" className="st-h2">How it works</h2>
            <StoryActs />
          </div>
        </section>

        <section className="bp-scene st-scene" aria-labelledby="st-praise-title" data-testid="st-praise">
          <div className="bp-wrap">
            <h2 id="st-praise-title" className="st-h2">Praise for previous work</h2>
            <StoryPraise />
          </div>
        </section>

        <section className="bp-scene st-scene st-final" aria-labelledby="st-final-title" data-testid="st-final">
          <div className="bp-wrap">
            <h2 id="st-final-title" className="st-h2">To be or not to be?</h2>
            <p className="st-body st-narrow">
              I’ve seen and read hundreds of plays and have never found one that celebrates businesses or the people who make them. I am determined to change that. But this is not for everyone. It only works if you have a compelling story and are prepared to hold up the mirror to nature, sharing the good as well as the bad.
            </p>
            <p className="st-closing" data-testid="st-closing">If that’s you, let’s bring the house down.</p>
            <BookACallLink testId="st-final-cta" />
          </div>
        </section>
      </main>
      <Footer />
      <FloatingCta heroRef={heroRef} testId="st-floating-cta" />
    </div>
  );
}
